'use client';

import { useRef } from 'react';
import { ArticleCardGrid } from '@/app_components/ArticleSections/ArticleCards/ArticleCardGrid';
import { StandardHeading } from '@/app_components/ArticleSections/Heading';
import { ActionableIcon } from '@/app_components/common/ActionableIcon';
import { Icon } from '@/app_components/common/Icon';
import type { ArticlePreviewGrid } from '@/app_lib/types/ArticlePreview';
import { cn } from '@/app_lib/utils/utils';
import { HorizontalSeparator } from '../ui/Separator';

interface ArticleSectionCarouselProps {
  articles: ArticlePreviewGrid[];
  className?: string;
  heading?: string;
  href?: string;
}

export const ArticleSectionCarousel = ({ articles, className, heading, href }: ArticleSectionCarouselProps) => {
  const scrollRef = useRef<HTMLDivElement>(null);

  const scrollTo = (direction: 'left' | 'right') => {
    const container = scrollRef.current;

    if (!container) {
      return;
    }

    const cardWidth = container.firstElementChild?.clientWidth || 0;
    const scrollAmount = direction === 'left' ? -cardWidth : cardWidth;

    container.scrollBy({ behavior: 'smooth', left: scrollAmount });
  };

  return (
    <div className={cn('flex flex-col gap-6', className)}>
      {heading && <StandardHeading href={href}>{heading}</StandardHeading>}
      <div
        className='
          grid grid-flow-col
          overflow-x-auto md:overflow-x-hidden
          scrollbar-hidden scroll-smooth
          snap-x snap-mandatory
          gap-4 md:gap-8 [--gap:theme(spacing.8)]
          auto-cols-[75%] md:auto-cols-[calc((100%-2*var(--gap))/3)] lg:auto-cols-[calc((100%-3*var(--gap))/4)]
          -mx-4 md:mx-0
          pr-4 md:pr-0
        '
        ref={scrollRef}
      >
        {articles.map((article) => (
          <ArticleCardGrid
            article={article}
            className='snap-start min-w-0 pl-4 md:pl-0'
            key={article.id}
          />
        ))}
      </div>
      <div className='flex flex-col gap-lg'>
        <div className='hidden md:flex ml-auto gap-lg'>
          <ActionableIcon
            className='p-2 border rounded-full stroke-helper bg-secondary'
            onClick={() => scrollTo('left')}
          >
            <Icon icon='ChevronLeft' />
          </ActionableIcon>
          <ActionableIcon
            className='p-2 border rounded-full stroke-helper bg-secondary'
            onClick={() => scrollTo('right')}
          >
            <Icon icon='ChevronRight' />
          </ActionableIcon>
        </div>
        <HorizontalSeparator className='stroke-helper' />
      </div>
    </div>
  );
};
